import { useTheme } from "client/contexts/ThemeProvider";
import { useToggle } from "client/hooks/utils/useToggle";
import type { CSSProperties } from "react";
import ReactSelect, { components, type CSSObjectWithLabel } from "react-select";
import { Translate } from "../Translate/Translate";
import { MobileSelect } from "./MobileSelect/MobileSelect";

export interface SelectProps<T> {
    options: readonly T[];
    handleClick: (option: T) => void;
    getOptionValue: (option: T) => string | number;
    value?: T;
    defaultValue?: T;
    getOptionLabel?: (option: T) => string;
    label?: string;
    labelClassNames?: string;
    description?: string;
    testKey?: string;
    mobileStyles?: {
        [key in keyof Styles]?: CSSProperties;
    };
}
interface Styles {
    control?: CSSObjectWithLabel;
    dropdownIndicator?: CSSObjectWithLabel;
    singleValue?: CSSObjectWithLabel;
}

export const Select = <T,>({
    label,
    options,
    handleClick,
    description,
    getOptionValue,
    getOptionLabel,
    defaultValue,
    labelClassNames,
    value,
    testKey: _testKey,
    mobileStyles,
}: SelectProps<T>) => {
    const testKey = _testKey ?? "select";
    const { isOpen: menuIsOpen, open: openMenu, onClose: closeMenu, toggle: toggleMenu } = useToggle();
    return (
        <div className="relative flex flex-col gap-2">
            {label && (
                <label className={`${labelClassNames}`} data-testid={`${testKey}-label`} htmlFor={testKey}>
                    {label}
                </label>
            )}

            <div className="absolute left-0 top-0">
                <span data-testid={`${testKey}-selected`} onClick={toggleMenu} className="bg-transparent p-1" />
            </div>
            <ReactSelect
                className="hidden lg:block my-react-select-container"
                options={options.map((option) => ({
                    label: getOptionLabel ? getOptionLabel(option) : `${getOptionValue(option)}`,
                    value: option,
                }))}
                menuPosition="absolute"
                noOptionsMessage={() => <Translate keyName="common:no-options-message" />}
                id={testKey}
                defaultValue={
                    defaultValue
                        ? {
                              label: getOptionLabel ? getOptionLabel(defaultValue) : defaultValue,
                              value: defaultValue,
                          }
                        : undefined
                }
                value={{
                    label: value && getOptionLabel ? getOptionLabel(value) : value ? `${getOptionValue(value)}` : "",
                    value: value,
                }}
                classNamePrefix="my-react-select"
                onChange={(value) => {
                    value?.value && handleClick(value.value);
                }}
                menuIsOpen={menuIsOpen}
                openMenuOnClick={true}
                onMenuOpen={openMenu}
                onMenuClose={closeMenu}
                components={{
                    Option: ({ children, ...props }) => {
                        return (
                            <components.Option {...props}>
                                <span
                                    data-testid={`${testKey}-option-${props.data.value ?? ""}`}
                                    onClick={() => {
                                        props.data.value && handleClick(props.data.value);
                                        closeMenu();
                                    }}
                                    className="bg-transparent"
                                />
                                {children}
                            </components.Option>
                        );
                    },
                }}
                menuShouldScrollIntoView={true}
            />

            <MobileSelect
                options={options}
                defaultValue={defaultValue}
                handleClick={handleClick}
                value={value}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                label={label}
                labelClassNames={labelClassNames}
                testKey={testKey}
                mobileStyles={mobileStyles}
            />

            {description && (
                <div className="text-gray-500 py-2" data-testid={`${testKey}-description`}>
                    {description}
                </div>
            )}
        </div>
    );
};
